import React, { useState, useCallback } from "react"
import { InputAdornment, TextField } from "@mui/material"
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined"
import CloseIcon from "@mui/icons-material/Close"
import ChipsScroller from "../chips/ChipsScroller"
import type { FiltersProps } from "../../types"
import "./styles/Filters.scss"
import { getCategoryIcon } from "./constants"
import { Search } from "@mui/icons-material"

const Filters: React.FC<FiltersProps> = ({ vibes, categories, selectedVibes, selectedCategories, onChange }) => {
  const [searchTerm, setSearchTerm] = useState("")

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const term = event.target.value
      setSearchTerm(term)
      onChange({
        vibes: selectedVibes,
        categories: selectedCategories,
        searchTerm: term,
      })
    },
    [selectedVibes, selectedCategories, onChange]
  )

  const handleVibeClick = (vibeId: string) => {
    if (vibeId === "all") {
      onChange({ vibes: [], categories: selectedCategories, searchTerm })
    } else {
      const vibe = vibes.find((v) => v.id === vibeId)
      if (vibe) {
        onChange({ vibes: [vibe], categories: selectedCategories, searchTerm })
      }
    }
  }

  const handleCategoryClick = (categoryId: string) => {
    if (categoryId === "all") {
      onChange({ vibes: selectedVibes, categories: [], searchTerm })
    } else {
      const category = categories.find((c) => c.id === categoryId)
      if (category) {
        onChange({ vibes: selectedVibes, categories: [category], searchTerm })
      }
    }
  }

  const vibeChips = vibes
    .filter((vibe) => !selectedVibes.some((selected) => selected.id === vibe.id))
    .map((vibe) => ({
      id: vibe.id,
      text: vibe.name,
      icon: null,
    }))

  const categoryChips = categories
    .filter((category) => !selectedCategories.some((selected) => selected.id === category.id))
    .map((category) => {
      const IconComponent = getCategoryIcon(category.name)
      return {
        id: category.id,
        text: category.name,
        icon: <IconComponent style={{ fontSize: "14px" }} />,
      }
    })

  const selectedVibeChip =
    selectedVibes.length === 0
      ? {
          id: "all",
          text: "All Vibes",
          icon: <BoltOutlinedIcon style={{ fontSize: "16px" }} />,
          showClose: false,
        }
      : {
          id: selectedVibes[0].id,
          text: selectedVibes[0].name,
          icon: <BoltOutlinedIcon style={{ fontSize: "16px" }} />,
          closeIcon: <CloseIcon style={{ fontSize: "16px" }} />,
          showClose: true,
        }

  const selectedCategoryChip =
    selectedCategories.length === 0
      ? {
          id: "all",
          text: "All",
          icon: getCategoryIcon("default")({ style: { fontSize: "14px" } }),
          showClose: false,
        }
      : {
          id: selectedCategories[0].id,
          text: selectedCategories[0].name,
          icon: getCategoryIcon(selectedCategories[0].name)({ style: { fontSize: "14px" } }),
          closeIcon: <CloseIcon style={{ fontSize: "16px" }} />,
          showClose: true,
        }

  return (
    <div className="filters">
      <div className="filter-section">
        <TextField
          key="search-field"
          fullWidth
          placeholder="Search by name, vibe, etc..."
          value={searchTerm}
          onChange={handleSearch}
          variant="outlined"
          size="small"
          className="search-field"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" key="search-icon">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>

      <div className="filter-section">
        <ChipsScroller selectedChip={selectedCategoryChip} chips={categoryChips} onChipClick={handleCategoryClick} size="small" />
      </div>

      <div className="filter-section">
        <ChipsScroller selectedChip={selectedVibeChip} chips={vibeChips} onChipClick={handleVibeClick} size="small" />
      </div>
    </div>
  )
}

export default Filters
